import React from 'react';

import {
  DayStatus,
  statusSpec,
  CalendarRoot,
  NavButton,
  DaysStrip,
  DayCard,
  DayCardTop,
  Weekday,
  DayNumber,
  StatusBadge,
  EmptyMessage,
} from './styles';

export type { DayStatus } from './styles';
export { statusSpec } from './styles';

/**
 * Um dia exibido na faixa do calendário.
 *
 * O consumidor monta os dias (geralmente a partir da resposta de
 * disponibilidade de rotas da API) e os passa prontos — o componente é
 * apenas apresentacional.
 */
export interface DayItem {
  /** Data ISO (YYYY-MM-DD). Usada como chave de seleção. */
  date: string;
  /** Nome do dia da semana, ex: "Segunda". */
  weekdayLabel: string;
  /** Número do dia, ex: 28. */
  dayNumber: number;
  /** Status da rota nesse dia. */
  status: DayStatus;
  /** Sobrescreve o texto do badge (default deriva do status). */
  badgeLabel?: string;
  /** Destaca o dia de hoje (borda escura). */
  isToday?: boolean;
}

export interface WeekCalendarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  /** Dias a renderizar na faixa, em ordem. */
  days: DayItem[];
  /** Data ISO atualmente selecionada. */
  selectedDate?: string | null;
  /** Disparado ao clicar num dia selecionável (não-indisponível). */
  onSelectDate?: (day: DayItem) => void;
  /** Disparado ao clicar na seta esquerda (semana/período anterior). */
  onPrev?: () => void;
  /** Disparado ao clicar na seta direita (semana/período seguinte). */
  onNext?: () => void;
  /** Desabilita a seta esquerda. @default false */
  prevDisabled?: boolean;
  /** Desabilita a seta direita. @default false */
  nextDisabled?: boolean;
  /** Mensagem exibida quando `days` está vazio. */
  emptyMessage?: string;
  /** Destaca a faixa em vermelho (ex.: validação ao avançar). @default false */
  error?: boolean;
}

// ── Chevrons ─────────────────────────────────────────────────────────────────

const ChevronLeft = (): React.ReactElement => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M10 13L5 8l5-5"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRight = (): React.ReactElement => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M6 3l5 5-5 5"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Component ────────────────────────────────────────────────────────────────

/**
 * WeekCalendar — faixa horizontal de dias para a escolha da data de entrega.
 *
 * Cada dia é um card com nome do dia, número e um badge de status:
 *  - "Dia de rota"   (azul)   → dia em que a equipe passa na região
 *  - "Fora de rota"  (preto)  → dia válido, mas fora da rota regular
 *  - "Indisponivel"  (cinza)  → indisponível, não selecionável
 *
 * Componente apresentacional e independente do seletor de mês: a navegação
 * entre períodos é delegada via `onPrev`/`onNext`.
 *
 * @example
 * const days: DayItem[] = [
 *   { date: '2026-05-28', weekdayLabel: 'Segunda', dayNumber: 28, status: 'route' },
 *   { date: '2026-05-29', weekdayLabel: 'Terça',   dayNumber: 29, status: 'route' },
 * ];
 * <WeekCalendar
 *   days={days}
 *   selectedDate={selected}
 *   onSelectDate={(d) => setSelected(d.date)}
 *   onPrev={loadPrevWeek}
 *   onNext={loadNextWeek}
 * />
 */
export const WeekCalendar = ({
  days,
  selectedDate = null,
  onSelectDate,
  onPrev,
  onNext,
  prevDisabled = false,
  nextDisabled = false,
  emptyMessage = 'Nenhum dia disponível para este período',
  error = false,
  ...rest
}: WeekCalendarProps): React.ReactElement => {
  const handleSelect = (day: DayItem): void => {
    if (day.status === 'unavailable') return;
    onSelectDate?.(day);
  };

  return (
    <CalendarRoot
      role="group"
      aria-label="Datas de entrega"
      $error={error}
      {...rest}
    >
      <NavButton
        type="button"
        onClick={onPrev}
        disabled={prevDisabled || !onPrev}
        aria-label="Período anterior"
      >
        <ChevronLeft />
      </NavButton>

      {days.length === 0 ? (
        <EmptyMessage>{emptyMessage}</EmptyMessage>
      ) : (
        <DaysStrip>
          {days.map((day) => {
            const selected = selectedDate === day.date;
            const label = day.badgeLabel ?? statusSpec[day.status].label;
            return (
              <DayCard
                key={day.date}
                type="button"
                $status={day.status}
                $selected={selected}
                $today={day.isToday ?? false}
                disabled={day.status === 'unavailable'}
                aria-pressed={selected}
                aria-label={`${day.weekdayLabel} ${day.dayNumber} — ${label}`}
                onClick={() => handleSelect(day)}
              >
                <DayCardTop>
                  <Weekday $status={day.status} $selected={selected}>
                    {day.weekdayLabel}
                  </Weekday>
                  <DayNumber $status={day.status} $selected={selected}>
                    {String(day.dayNumber).padStart(2, '0')}
                  </DayNumber>
                </DayCardTop>
                <StatusBadge $status={day.status}>{label}</StatusBadge>
              </DayCard>
            );
          })}
        </DaysStrip>
      )}

      <NavButton
        type="button"
        onClick={onNext}
        disabled={nextDisabled || !onNext}
        aria-label="Próximo período"
      >
        <ChevronRight />
      </NavButton>
    </CalendarRoot>
  );
};

export default WeekCalendar;
